import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { isAdmin, isEncargada, isBotica, isJuguteria, isCajero } from '../hooks/useSucursal';
import './ProductsMobile.css';

/* ── Helpers ───────────────────────────────────────────── */
const PILL_COLORS = [
    '#ef4444','#f97316','#eab308','#22c55e',
    '#3b82f6','#8b5cf6','#ec4899','#14b8a6','#64748b'
];
const getPillColor = (str = '') => {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
    return PILL_COLORS[Math.abs(h) % PILL_COLORS.length];
};
const fmt = (v) => `S/ ${parseFloat(v || 0).toFixed(2)}`;

const DIFICULTAD_COLORS = {
    principiante: { bg: '#ecfdf5', color: '#047857' },
    medio:        { bg: '#fefce8', color: '#b45309' },
    avanzado:     { bg: '#fef2f2', color: '#b91c1c' },
};

/* ════════════════════════════════════════════════════════
   ProductsMobile — Gestión de productos para celulares
   3 tabs: Lista | Buscar | Stats
════════════════════════════════════════════════════════ */
const ProductsMobile = ({
    products = [],
    stats = null,
    isLoading = false,
    filter = 'active',
    alertasLotes = null,
    sucursales = [],
    sucursalActiva = null,
    currentSucursalNombre = '',
    trashedCount = 0,
    onOpenActions,
    onCreate,
    onRefresh,
    onFilterChange,
    onSucursalChange,
}) => {

    const [mainTab, setMainTab]         = useState('lista');
    const [searchTerm, setSearchTerm]   = useState('');
    const [stockFilter, setStockFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [expandedProduct, setExpandedProduct] = useState(null);
    const [showSucursalSheet, setShowSucursalSheet] = useState(false);

    const canEdit   = isAdmin() || isEncargada();
    const admin     = isAdmin();
    const juguteria = isJuguteria();
    const botica    = isBotica();

    const uniqueCategories = useMemo(() =>
        [...new Set(products.map(p => p.category).filter(Boolean))].sort()
    , [products]);

    const filteredProducts = useMemo(() => {
        let result = products;
        if (searchTerm.trim().length >= 1) {
            const t = searchTerm.toLowerCase();
            result = result.filter(p =>
                p.name?.toLowerCase().includes(t) ||
                p.code?.toLowerCase().includes(t) ||
                p.category?.toLowerCase().includes(t) ||
                p.description?.toLowerCase().includes(t)
            );
        }
        if (stockFilter === 'low')  result = result.filter(p => p.stock > 0 && p.stock <= (p.min_stock || 5));
        if (stockFilter === 'out')  result = result.filter(p => p.stock <= 0);
        if (categoryFilter)         result = result.filter(p => p.category === categoryFilter);
        return result;
    }, [products, searchTerm, stockFilter, categoryFilter]);

    const getStockState = (p) => {
        if (p.stock <= 0)                  return 'out';
        if (p.stock <= (p.min_stock || 5)) return 'low';
        return 'ok';
    };
    const stockBadge = { ok: 'pmob-stock-ok', low: 'pmob-stock-low', out: 'pmob-stock-out' };

    const statCards = useMemo(() => ([
        { key: 'active',       label: 'Total',      icon: '📦', color: 'blue',   value: stats?.active ?? products.length },
        { key: 'low_stock',    label: 'Stock Bajo', icon: '🟡', color: 'warn',   value: stats?.low_stock ?? '—' },
        { key: 'out_of_stock', label: 'Agotados',   icon: '🔴', color: 'red',    value: stats?.out_of_stock ?? '—' },
        { key: 'categories',   label: 'Categorías', icon: '🏷️', color: 'purple', value: stats?.total_categories ?? uniqueCategories.length },
    ]), [stats, uniqueCategories, products.length]);

    const valorInventario = useMemo(() =>
        products.reduce((s, p) => s + (parseFloat(p.stock)||0)*(parseFloat(p.purchase_price||p.sale_price)||0), 0)
    , [products]);

    const porCategoria = useMemo(() => {
        const map = {};
        products.forEach(p => {
            const cat = p.category || 'Sin categoría';
            if (!map[cat]) map[cat] = { count: 0, value: 0 };
            map[cat].count++;
            map[cat].value += (parseFloat(p.stock)||0)*(parseFloat(p.purchase_price||p.sale_price)||0);
        });
        return Object.entries(map).sort((a, b) => b[1].count - a[1].count);
    }, [products]);

    return (
        <div className="pmob-root">

            {/* ── Top Bar ──────────────────────────────── */}
            <div className="pmob-topbar">
                <div className="pmob-topbar-left">
                    <span className="pmob-topbar-title">
                        {filter === 'active'
                            ? (juguteria ? '🧩 Productos' : botica ? '💊 Medicamentos' : '📦 Productos')
                            : '🗑️ Papelera'}
                    </span>
                    <span className="pmob-topbar-sub">
                        {currentSucursalNombre || (admin ? 'Catálogo global' : 'Mi inventario')}
                        {stats && filter === 'active' ? ` · ${stats.active || products.length} items` : ''}
                    </span>
                </div>
                <div className="pmob-topbar-right">
                    {admin && sucursales.length > 0 && (
                        <button className="pmob-suc-btn" onClick={() => setShowSucursalSheet(true)}>
                            🏪 {sucursalActiva ? (sucursales.find(s => s.id === sucursalActiva)?.nombre || 'Suc.') : 'Global'}
                        </button>
                    )}
                    <button className="pmob-topbar-icon-btn" onClick={onRefresh} title="Actualizar">↺</button>
                    {filter === 'active'
                        ? canEdit && <button className="pmob-topbar-btn--primary" onClick={onCreate}>+ Nuevo</button>
                        : <button className="pmob-topbar-icon-btn" onClick={() => onFilterChange('active')}>← Volver</button>
                    }
                </div>
            </div>

            {/* ── Loading bar ───────────────────────────── */}
            {isLoading && <div className="pmob-progress-bar"><div className="pmob-progress-fill" /></div>}

            {/* ── Alertas FEFO (Botica) ─────────────────── */}
            {botica && alertasLotes?.resumen?.total_alertas > 0 && (
                <div className="pmob-fefo-banner">
                    <span>🚦</span>
                    <div className="pmob-fefo-pills">
                        {alertasLotes.resumen.vencidos > 0 && <span className="pmob-fefo-pill pmob-fefo--vencido">⚫ Vencidos: {alertasLotes.resumen.vencidos}</span>}
                        {alertasLotes.resumen.criticos > 0 && <span className="pmob-fefo-pill pmob-fefo--critico">🔴 Críticos: {alertasLotes.resumen.criticos}</span>}
                        {alertasLotes.resumen.por_vencer > 0 && <span className="pmob-fefo-pill pmob-fefo--pv">🟡 Por vencer: {alertasLotes.resumen.por_vencer}</span>}
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════
                TAB: LISTA
            ══════════════════════════════════════════ */}
            {mainTab === 'lista' && (
                <div className="pmob-content">
                    <div className="pmob-quick-filters">
                        <button className={`pmob-qf-btn ${stockFilter === 'all' ? 'active' : ''}`} onClick={() => setStockFilter('all')}>Todos</button>
                        <button className={`pmob-qf-btn pmob-qf-warn ${stockFilter === 'low' ? 'active' : ''}`} onClick={() => setStockFilter(stockFilter === 'low' ? 'all' : 'low')}>
                            🟡 Bajo{stats?.low_stock > 0 ? ` (${stats.low_stock})` : ''}
                        </button>
                        <button className={`pmob-qf-btn pmob-qf-red ${stockFilter === 'out' ? 'active' : ''}`} onClick={() => setStockFilter(stockFilter === 'out' ? 'all' : 'out')}>
                            🔴 Agotados{stats?.out_of_stock > 0 ? ` (${stats.out_of_stock})` : ''}
                        </button>
                    </div>

                    {(stockFilter !== 'all' || categoryFilter) && (
                        <div className="pmob-filter-info">
                            {filteredProducts.length} de {products.length} productos
                            <button className="pmob-clear-filter" onClick={() => { setStockFilter('all'); setCategoryFilter(''); }}>✕ Limpiar</button>
                        </div>
                    )}

                    {filteredProducts.length === 0 ? (
                        <div className="pmob-empty"><span>📭</span><p>{isLoading ? 'Cargando...' : 'No hay productos'}</p></div>
                    ) : (
                        <div className="pmob-prod-list">
                            {filteredProducts.map(product => {
                                const ss = getStockState(product);
                                const isExp = expandedProduct === product.id;
                                return (
                                    <div key={product.id} className={`pmob-prod-card${isExp ? ' expanded' : ''}`}>
                                        <div className="pmob-prod-card-main" onClick={() => setExpandedProduct(isExp ? null : product.id)}>
                                            <div className="pmob-prod-avatar" style={{ background: getPillColor(product.name) }}>
                                                {juguteria ? '🧩' : botica ? '💊' : (product.name||'?')[0].toUpperCase()}
                                            </div>
                                            <div className="pmob-prod-info">
                                                <div className="pmob-prod-name">{product.name}</div>
                                                <div className="pmob-prod-meta">
                                                    {product.code && <span className="pmob-prod-code">{product.code}</span>}
                                                    {product.category && (
                                                        <span className="pmob-prod-cat" style={{ background: `${getPillColor(product.category)}22`, color: getPillColor(product.category) }}>
                                                            {product.category}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="pmob-prod-right">
                                                <span className="pmob-prod-price">{fmt(product.sale_price)}</span>
                                                <span className={`pmob-stock-badge ${stockBadge[ss]}`}>
                                                    {ss === 'out' ? 'AGOTADO' : `${Math.floor(product.stock)}${ss === 'low' ? ' ⚠️' : ''}`}
                                                </span>
                                            </div>
                                        </div>
                                        {juguteria && (product.numero_piezas || product.nivel_dificultad) && (
                                            <div className="pmob-jug-tags">
                                                {product.numero_piezas && <span className="pmob-jug-tag">🧩 {product.numero_piezas} piezas</span>}
                                                {product.nivel_dificultad && (
                                                    <span className="pmob-jug-tag" style={{ background: DIFICULTAD_COLORS[product.nivel_dificultad]?.bg || '#f1f5f9', color: DIFICULTAD_COLORS[product.nivel_dificultad]?.color || '#334155' }}>
                                                        {product.nivel_dificultad}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {isExp && (
                                            <div className="pmob-prod-detail">
                                                <div className="pmob-detail-grid">
                                                    <div className="pmob-detail-item"><span className="pmob-detail-label">Stock</span><span className={`pmob-detail-val ${ss === 'out' ? 'red' : ss === 'low' ? 'warn' : 'green'}`}>{product.unit === 'kg' ? parseFloat(product.stock).toFixed(2) : Math.floor(product.stock)} {product.unit||'un'}</span></div>
                                                    <div className="pmob-detail-item"><span className="pmob-detail-label">Mínimo</span><span className="pmob-detail-val">{product.min_stock||0} {product.unit||'un'}</span></div>
                                                    <div className="pmob-detail-item"><span className="pmob-detail-label">Precio venta</span><span className="pmob-detail-val green">{fmt(product.sale_price)}</span></div>
                                                    {canEdit && product.purchase_price && <div className="pmob-detail-item"><span className="pmob-detail-label">Precio compra</span><span className="pmob-detail-val">{fmt(product.purchase_price)}</span></div>}
                                                    {product.unit && <div className="pmob-detail-item"><span className="pmob-detail-label">Unidad</span><span className="pmob-detail-val">{product.unit}</span></div>}
                                                    {botica && product.laboratorio && <div className="pmob-detail-item"><span className="pmob-detail-label">Laboratorio</span><span className="pmob-detail-val">{product.laboratorio}</span></div>}
                                                    {botica && product.principio_activo && <div className="pmob-detail-item"><span className="pmob-detail-label">P. Activo</span><span className="pmob-detail-val">{product.principio_activo}</span></div>}
                                                </div>
                                                {product.description && <div className="pmob-detail-desc">{product.description}</div>}
                                                <div className="pmob-detail-actions">
                                                    <button className="pmob-action-btn pmob-action-btn--view" onClick={() => { setExpandedProduct(null); onOpenActions(product); }}>
                                                        {canEdit ? '✏️ Editar / Acciones' : '👁️ Ver Acciones'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {admin && filter === 'active' && trashedCount > 0 && (
                        <button className="pmob-trash-btn" onClick={() => onFilterChange('inactive')}>
                            🗑️ Ver Papelera <span className="pmob-trash-badge">{trashedCount}</span>
                        </button>
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════════
                TAB: BUSCAR
            ══════════════════════════════════════════ */}
            {mainTab === 'buscar' && (
                <div className="pmob-content">
                    <div className="pmob-searchbox">
                        <span className="pmob-search-icon">🔍</span>
                        <input type="text" className="pmob-search-input" placeholder="Nombre, código, categoría..."
                            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} autoFocus />
                        {searchTerm && <button className="pmob-search-clear" onClick={() => setSearchTerm('')}>✕</button>}
                    </div>

                    {uniqueCategories.length > 0 && (
                        <>
                            <div className="pmob-section-header">Por categoría</div>
                            <div className="pmob-cat-chips">
                                <button className={`pmob-cat-chip ${!categoryFilter ? 'active' : ''}`} onClick={() => setCategoryFilter('')}>Todas</button>
                                {uniqueCategories.map(cat => (
                                    <button key={cat}
                                        className={`pmob-cat-chip ${categoryFilter === cat ? 'active' : ''}`}
                                        style={categoryFilter === cat ? { background: getPillColor(cat), color: '#fff', borderColor: getPillColor(cat) } : {}}
                                        onClick={() => setCategoryFilter(categoryFilter === cat ? '' : cat)}>
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    <div className="pmob-section-header">
                        {searchTerm || categoryFilter ? `${filteredProducts.length} resultado(s)` : `${products.length} productos`}
                    </div>

                    {filteredProducts.length === 0 ? (
                        <div className="pmob-empty"><span>🔍</span><p>Sin resultados</p></div>
                    ) : (
                        <div className="pmob-prod-list">
                            {filteredProducts.map(product => {
                                const ss = getStockState(product);
                                return (
                                    <div key={product.id} className="pmob-prod-card pmob-prod-card--compact" onClick={() => onOpenActions(product)}>
                                        <div className="pmob-prod-avatar" style={{ background: getPillColor(product.name) }}>
                                            {juguteria ? '🧩' : botica ? '💊' : (product.name||'?')[0].toUpperCase()}
                                        </div>
                                        <div className="pmob-prod-info">
                                            <div className="pmob-prod-name">{product.name}</div>
                                            <div className="pmob-prod-meta">
                                                {product.code && <span className="pmob-prod-code">{product.code}</span>}
                                                {botica && product.principio_activo && <span className="pmob-prod-code">{product.principio_activo}</span>}
                                            </div>
                                        </div>
                                        <div className="pmob-prod-right">
                                            <span className="pmob-prod-price">{fmt(product.sale_price)}</span>
                                            <span className={`pmob-stock-badge ${stockBadge[ss]}`}>{ss === 'out' ? '❌' : `${Math.floor(product.stock)} ${product.unit||'u'}`}</span>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ══════════════════════════════════════════
                TAB: STATS
            ══════════════════════════════════════════ */}
            {mainTab === 'stats' && (
                <div className="pmob-content">
                    <div className="pmob-section-header">📊 Resumen de inventario</div>
                    <div className="pmob-stat-grid">
                        {statCards.map(card => (
                            <div key={card.key} className={`pmob-stat-card pmob-stat--${card.color}`}>
                                <span className="pmob-stat-icon">{card.icon}</span>
                                <span className="pmob-stat-val">{card.value}</span>
                                <span className="pmob-stat-lbl">{card.label}</span>
                            </div>
                        ))}
                    </div>

                    <div className="pmob-valor-card">
                        <div className="pmob-valor-label">💰 Valor del inventario (precio compra)</div>
                        <div className="pmob-valor-amount">{fmt(valorInventario)}</div>
                    </div>

                    {porCategoria.length > 0 && (
                        <>
                            <div className="pmob-section-header">🏷️ Por categoría</div>
                            <div className="pmob-cat-list">
                                {porCategoria.slice(0, 10).map(([cat, data]) => {
                                    const pct = products.length > 0 ? (data.count / products.length) * 100 : 0;
                                    return (
                                        <div key={cat} className="pmob-cat-row">
                                            <div className="pmob-cat-row-top">
                                                <div className="pmob-cat-dot" style={{ background: getPillColor(cat) }} />
                                                <span className="pmob-cat-name">{cat}</span>
                                                <span className="pmob-cat-count">{data.count}</span>
                                                <span className="pmob-cat-val">{fmt(data.value)}</span>
                                            </div>
                                            <div className="pmob-cat-bar">
                                                <div className="pmob-cat-bar-fill" style={{ width: `${pct}%`, background: getPillColor(cat) }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}

                    {botica && alertasLotes?.alertas?.length > 0 && (
                        <>
                            <div className="pmob-section-header">🚦 Alertas FEFO</div>
                            <div className="pmob-prod-list">
                                {alertasLotes.alertas.slice(0, 8).map((alerta, i) => {
                                    const colorMap = { VENCIDO: 'pmob-fefo-card--vencido', CRITICO: 'pmob-fefo-card--critico', POR_VENCER: 'pmob-fefo-card--pv' };
                                    return (
                                        <div key={i} className={`pmob-fefo-card ${colorMap[alerta.estado_vencimiento] || ''}`}>
                                            <div className="pmob-fefo-card-name">{alerta.nombre_producto}</div>
                                            <div className="pmob-fefo-card-meta">
                                                <span>Vence: {new Date(alerta.fecha_vencimiento + 'T00:00:00').toLocaleDateString('es-PE', { day:'2-digit', month:'short', year:'numeric' })}</span>
                                                <span className="pmob-fefo-estado">{alerta.estado_vencimiento}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ── Navegación principal ─────────────────── */}
            <div className="pmob-main-nav">
                <button className={`pmob-nav-item ${mainTab === 'lista' ? 'active' : ''}`} onClick={() => setMainTab('lista')}>
                    <span className="pmob-nav-icon">📋</span>
                    <span className="pmob-nav-label">Lista</span>
                    {(stats?.low_stock > 0 || stats?.out_of_stock > 0) && mainTab !== 'lista' && (
                        <span className="pmob-nav-alert" />
                    )}
                </button>
                <button className={`pmob-nav-item ${mainTab === 'buscar' ? 'active' : ''}`} onClick={() => setMainTab('buscar')}>
                    <span className="pmob-nav-icon">🔍</span>
                    <span className="pmob-nav-label">Buscar</span>
                </button>
                <button className={`pmob-nav-item ${mainTab === 'stats' ? 'active' : ''}`} onClick={() => setMainTab('stats')}>
                    <span className="pmob-nav-icon">📊</span>
                    <span className="pmob-nav-label">Stats</span>
                </button>
            </div>

            {/* ── Bottom Sheet: sucursal ────────────────── */}
            {showSucursalSheet && (
                <div className="pmob-sheet-overlay" onClick={() => setShowSucursalSheet(false)}>
                    <div className="pmob-sheet" onClick={e => e.stopPropagation()}>
                        <div className="pmob-sheet-handle" />
                        <div className="pmob-sheet-title">Seleccionar sucursal</div>
                        <div className="pmob-sheet-list">
                            <button className={`pmob-sheet-item ${sucursalActiva === null ? 'active' : ''}`} onClick={() => { onSucursalChange(null); setShowSucursalSheet(false); }}>
                                🌐 Todas las sucursales
                            </button>
                            {sucursales.map(s => (
                                <button key={s.id} className={`pmob-sheet-item ${sucursalActiva === s.id ? 'active' : ''}`} onClick={() => { onSucursalChange(s.id); setShowSucursalSheet(false); }}>
                                    {s.es_offline ? '📴' : '🏪'} {s.nombre}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProductsMobile;
